import { reactive } from "vue";
import type { ArchiveDTO, UpdateArchiveSettingsRequest } from "@barkup/shared";
import { getGroupIdFromUrl, getViewerProfile } from "./liff.js";
import { resolveArchive, updateArchiveSettings } from "./api.js";

/**
 * Minimal shared state — no Pinia, the app is small enough (one archive in
 * view at a time) that a single reactive object is easier to follow than a
 * store library.
 */
export const store = reactive<{
  archive: ArchiveDTO | null;
  /**
   * The archive this LIFF session was launched into (personal for a 1:1
   * launch, the group for a ?gid= launch). Kept separate from `archive` so a
   * user who dives into another group's files from GroupManagementView can
   * jump back to where they started in one tap, without a re-resolve —
   * see selectGroupArchive / returnToLaunchArchive.
   */
  launchArchive: ArchiveDTO | null;
  loading: boolean;
  error: string | null;
  profile: { displayName: string; pictureUrl: string | null } | null;
  /** Bumped by the header search icon; FilterBar watches this to focus its input. */
  searchFocusToken: number;
}>({
  archive: null,
  launchArchive: null,
  loading: true,
  error: null,
  profile: null,
  searchFocusToken: 0,
});

/**
 * True when the user has stepped into a group archive other than the one this
 * session launched into (via GroupManagementView). The header uses this to
 * offer a "back to my archive" switch — it's the only way out of a group
 * context that wasn't the launch context.
 */
export function isViewingOtherArchive(): boolean {
  return !!store.archive && !!store.launchArchive && store.archive.id !== store.launchArchive.id;
}

/**
 * Switch the whole app to a group the user picked in GroupManagementView. The
 * list endpoint already returned a full ArchiveDTO (membership verified), so
 * every archive-scoped view (คลัง / ถังขยะ / ตั้งค่า) re-keys off store.archive.id
 * and starts operating on that group — no extra resolve needed.
 */
export function selectGroupArchive(archive: ArchiveDTO): void {
  store.archive = archive;
}

/** Return to the archive this session launched into (see selectGroupArchive). */
export function returnToLaunchArchive(): void {
  if (store.launchArchive) store.archive = store.launchArchive;
}

export function requestSearchFocus(): void {
  store.searchFocusToken += 1;
}

/** Persists a settings patch and merges the server's (authoritative) result back in. */
export async function updateSettings(patch: UpdateArchiveSettingsRequest): Promise<void> {
  if (!store.archive) return;
  store.archive = await updateArchiveSettings(store.archive.id, patch);
}

/**
 * Resolves the archive this LIFF session should show: the group archive named
 * by the link's ?gid= param (see liff.ts getGroupIdFromUrl), otherwise the
 * caller's own 1:1 archive. The actual access decision is made server-side
 * (DESIGN.md §5) — this just decides which archiveId to *ask about*.
 */
export async function loadCurrentArchive(): Promise<void> {
  store.loading = true;
  store.error = null;
  try {
    const groupId = getGroupIdFromUrl();
    const [archive, profile] = await Promise.all([
      groupId
        ? resolveArchive({ ownerType: "GROUP", lineOwnerId: groupId })
        : resolveArchive({ ownerType: "USER" }),
      getViewerProfile(),
    ]);
    store.archive = archive;
    store.launchArchive = archive;
    store.profile = profile;
  } catch (err) {
    store.archive = null;
    store.launchArchive = null;
    store.error = err instanceof Error ? err.message : "โหลดคลังไม่สำเร็จ";
  } finally {
    store.loading = false;
  }
}
